import { useCallback, useContext, useEffect, useState } from 'react'
import { baseApolloClient } from 'src/services/apollo/client'
import {
  ListMessagesDocument,
  type Message,
} from 'src/services/apollo/generated/graphql'
import { useCommentsScrollContext } from './commentsScroll'
import { CommentContext } from './context'

function flashAndScroll(el: HTMLElement) {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  // Cancel any in-flight flash so rapid clicks don't stack.
  el.getAnimations().forEach(a => a.cancel())
  const filled = 'rgb(59 130 246 / 0.18)'
  const cleared = 'rgb(59 130 246 / 0)'
  el.animate(
    [
      {
        backgroundColor: cleared,
        boxShadow: `0 0 0 0 ${cleared}`,
        borderRadius: '0px',
      },
      {
        backgroundColor: filled,
        boxShadow: `0 0 0 8px ${filled}`,
        borderRadius: '8px',
        offset: 0.15,
      },
      {
        backgroundColor: filled,
        boxShadow: `0 0 0 8px ${filled}`,
        borderRadius: '8px',
        offset: 0.6,
      },
      {
        backgroundColor: cleared,
        boxShadow: `0 0 0 0 ${cleared}`,
        borderRadius: '0px',
      },
    ],
    { duration: 1800, easing: 'ease-out' },
  )
  return true
}

/** Scrolls to a comment if it's in the loaded list; otherwise chain-loads
 * the gap (`time: gte parent.createdAt, lt oldest.createdAt`) via a single
 * query, merges it into the cache, then scrolls. */
export function useScrollToComment(
  parent: Pick<Message, 'id' | 'topicId' | 'createdAt'> | undefined,
) {
  const ctx = useContext(CommentContext)
  const scrollCtx = useCommentsScrollContext()
  const [loading, setLoading] = useState(false)
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null)

  // Wait for the merged cache to surface in ctx.messages (and thus for the
  // matching CommentItem to register its ref) before scrolling. rAF isn't
  // reliable — the Apollo→React cycle can take more than one frame.
  useEffect(() => {
    if (!pendingScrollId || !ctx) return
    const el = scrollCtx?.commentRefs.get(pendingScrollId)
    if (el) {
      flashAndScroll(el)
      setPendingScrollId(null)
      return
    }
    // Bail out if the parent never appears.
    const t = window.setTimeout(() => setPendingScrollId(null), 3000)
    return () => window.clearTimeout(t)
  }, [pendingScrollId, ctx, scrollCtx])

  const onPress = useCallback(async () => {
    if (!ctx || !parent) return

    const loadedEl = scrollCtx?.commentRefs.get(parent.id)
    if (loadedEl) {
      flashAndScroll(loadedEl)
      return
    }

    const oldest = ctx.messages[ctx.messages.length - 1]
    if (!oldest) return

    setLoading(true)
    try {
      const result = await baseApolloClient.query({
        query: ListMessagesDocument,
        variables: {
          topicId: parent.topicId,
          time: { gte: parent.createdAt, lt: oldest.createdAt },
          last: 500,
        },
        fetchPolicy: 'network-only',
      })

      const newEdges = (result.data?.messages.edges ?? []).filter(
        e => !!e?.node,
      )
      if (newEdges.length === 0) return

      const totalCount = ctx.totalCount
      baseApolloClient.cache.updateQuery(
        {
          query: ListMessagesDocument,
          variables: { topicId: parent.topicId },
        },
        data => {
          if (!data) return data
          const existingEdges = data.messages.edges ?? []
          const newIds = new Set(newEdges.map(e => e?.node?.id))
          const deduped = existingEdges.filter(
            e => e?.node?.id && !newIds.has(e.node.id),
          )
          const merged = [...newEdges, ...deduped]
          // Force hasPreviousPage off when we've loaded everything, otherwise
          // "Load more" sticks around after chain-loading the very-first comment.
          const reachedEnd = totalCount != null && merged.length >= totalCount
          return {
            ...data,
            messages: {
              ...data.messages,
              edges: merged,
              pageInfo: {
                ...data.messages.pageInfo,
                startCursor:
                  newEdges[0]?.cursor ?? data.messages.pageInfo.startCursor,
                hasPreviousPage: reachedEnd
                  ? false
                  : data.messages.pageInfo.hasPreviousPage,
              },
            },
          }
        },
      )

      setPendingScrollId(parent.id)
    } catch (err) {
      console.warn('Failed to load comments up to parent', err)
    } finally {
      setLoading(false)
    }
  }, [ctx, scrollCtx, parent])

  return { onPress, loading }
}
